import { api } from './client';

/**
 * ============================================================================
 * THE ADDITIONAL-SERVICES CLIENT — §17 / Appendix L §2.11, F17
 * ============================================================================
 *
 *   GET  /pa/services/{id}?contract_id=            — §17a/b/c: the three lists
 *   POST /pa/services/{id}/connect?contract_id=    — §17b: «Подключить» / «Заказать»
 *   POST /pa/services/{id}/disconnect?contract_id= — §17a: «Отключить»
 *
 * It mirrors the wire structs in `backend/internal/personalarea/services_handlers.go`,
 * pinned BOTH WAYS to `api/openapi.yaml` by `src/api/wire-spec.test.ts`.
 *
 * ============================================================================
 * ⚠ FOUR RULES THIS MODULE AND ITS SCREEN LIVE UNDER
 * ============================================================================
 *
 * 1. **«ОТКЛЮЧИТЬ» STOPS AND NEVER REFUNDS, AND THE SCREEN MUST SAY SO.** The route writes
 *    `valid_to = now`: the future stops, the past is kept, nothing is returned. Its
 *    operator-side neighbour — `accounts.RemoveService` — emits an event whose billing
 *    reactor REFUNDS EVERY KOPECK the instance was ever charged, which for an eight-month
 *    router rental is the whole rental. The two actions are one word apart in Russian, so a
 *    confirmation dialog that promised a refund would be promising the other one.
 *
 * 2. **THE CONTRACT TRAVELS IN THE QUERY STRING ON EVERY VERB, INCLUDING THE POSTS.**
 *    `Service.moneyScope` is the ONE ownership check on this listener and it reads
 *    `?contract_id=`. A client that put it in a body would be answered `404` by both write
 *    routes — a missing parameter is refused exactly like a foreign one — so the failure
 *    would read as a permissions fault rather than as a client bug. `wire-spec.test.ts`
 *    pins that every function here sends it.
 *
 * 3. **`payable_now` IS THE ENGINE'S FIGURE AND IS NEVER `cost + connection_cost`.** For a
 *    LINKED service (Appendix H §3.14) the connect charges the PRORATED REMAINDER of the
 *    running period plus the connection cost, so the price-list sum overstates a mid-month
 *    connection — and the refusal quotes the prorated figure, so a screen showing the sum
 *    would be refused for an amount it never named. ~~`money-arithmetic.test.ts` makes the
 *    addition unwritable~~ *(corrected 2026-08-12, W4 P3, by MEASURING it rather than by
 *    reading the guard's own header: the sum was written into the screen as
 *    `(cost ?? 0) + (connection_cost ?? 0)` — the shape a NULLABLE price invites — and that
 *    guard stayed GREEN, because its pattern could not begin a match on an opening
 *    parenthesis. It has been widened and now bites; the claim was false for as long as it
 *    stood.)* The guard is the mechanical half; this note says why the field exists.
 *
 * 4. **NULL IS NOT ZERO, on all three money fields.** A tariff with no cost row at this
 *    date sends `null`, and «0,00 руб» there advertises a service the engine cannot charge.
 *    The screen renders «не указана».
 *
 * ⚠ AND `to_date` IS THE **EXCLUSIVE** BOUND, STORED AND READ BACK VERBATIM. The instance
 * is live strictly BEFORE that day — `account_service.valid_to`'s meaning everywhere else in
 * this system, and what the admin card shows for the same row. Nothing in this module or its
 * screen adds or subtracts a day: a date translated on one leg of a round trip is a field
 * that does not read back as it was written, which is how a client and a server come to
 * disagree about a day. The Russian wording is what carries «не включая», not arithmetic.
 */

/** `PAService` — one row of §17's three lists: an instance he holds, or one he may order. */
export interface PAService {
  /**
   * The instance, and ZERO on an AVAILABLE row — which has no instance yet. That is what
   * «available» means, and it is why `/disconnect` names this and `/connect` names
   * `service_in_tariff_id`.
   */
  account_service_id: number;
  /** The CATALOGUE row — what `/connect` names. */
  service_in_tariff_id: number;
  name: string;
  /** §17's «Тарифный план» — on a multitariff Account, not necessarily the primary one. */
  tariff_name: string;
  description: string;
  /**
   * «periodic» | «one_time» — §2.11's «Type». It decides the BUTTON («Подключить» vs
   * «Заказать») and, more importantly, whether the row may be disconnected at all: a
   * one-time charge has already posted and there is no future to stop.
   */
  charge_type: string;
  /**
   * `billing_anchor = align_to_cycle`: part of the Account's subscription PACK (Appendix H
   * §3.14). It is the difference between a purchase priced from the price list and one
   * priced by the engine — see `payable_now`.
   */
  linked: boolean;
  /** §2.11's «recorded as self-assigned by the Subscriber». Always false on an available row. */
  self_assigned: boolean;
  quantity: number;
  /** Civil date in the OPERATOR's zone, null on an available row. */
  from_date: string | null;
  /** Civil date, null while the instance has no end. ⚠ EXCLUSIVE — see the file header. */
  to_date: string | null;
  /** The resolved per-service status (Appendix C §2.5), "" on an available row. */
  status: string;
  /** MINOR UNITS, the price-list figure. NULL is «no cost row configured», never free. */
  cost: number | null;
  connection_cost: number | null;
  /** MINOR UNITS, from the charging engine. Available rows only. NULL ≠ free — rule 3. */
  payable_now: number | null;
}

/** `PAServices` — the three lists of §17a/b/c for one Account, in ONE read. */
export interface PAServices {
  account_id: number;
  login: string;
  /** §17a — live instances. Only a `periodic` one is disconnectable. */
  connected: PAService[];
  /**
   * §17b. EMPTY IS ORDINARY: `service_in_tariff.subscriber_orderable` defaults FALSE, so an
   * installation whose operator has ticked nothing offers nothing — a configuration and not
   * a fault. The screen says which of the two it is.
   */
  available: PAService[];
  /** §17c — instances that are over. */
  history: PAService[];
  /**
   * ⚠ WHAT THE ORDER FORM MAY ASK FOR, FROM THE SERVER. §17d draws «Период действия услуги»
   * as a from–to pair and only the END is accepted: the charge is immediate, so a future
   * start would take money now for a period that has not begun. The field exists so the form
   * is TOLD rather than left to discover it — and so a build that stopped accepting the end
   * date could take the control away without a client release.
   */
  ordering_supports_end_date: boolean;
}

/** `PAConnectServiceRequest` — the body of §17b's «Подключить» / «Заказать». */
export interface PAConnectServiceRequest {
  service_in_tariff_id: number;
  /** §17d's «Количество». Absent or ≤ 0 means 1 on the server. */
  quantity?: number;
  /** YYYY-MM-DD, the EXCLUSIVE end of the window. Absent means «no end». */
  to_date?: string;
}

/** `PADisconnectServiceRequest` — the body of §17a's «Отключить». */
export interface PADisconnectServiceRequest {
  account_service_id: number;
}

/**
 * The five figures `409 accounts.service_assignment_unfunded` carries in `details`.
 *
 * ⚠ THEY ARE DECIMAL STRINGS AND NOT MINOR UNITS, unlike every other amount on this
 * surface, because they come from the engine's own `money.Amount` rendering inside a generic
 * error envelope. So they are printed VERBATIM: `formatMinor` would read «600.00» as six
 * hundred kopecks, and a screen that "converted" them would quote a figure a hundred times
 * too small for the one thing the subscriber must pay.
 *
 * ⚠ AND IT IS DELIBERATELY NOT IN `wire-spec.test.ts`'s INTERFACE LIST. `Error.details` is a
 * free-form object in `api/openapi.yaml` with no named schema, so there is nothing to pin it
 * against — and a pin against an invented schema name would throw rather than assert. The
 * shape is instead asserted by the screen's own arm, which renders a real 409 body.
 */
export interface PAUnfundedDetails {
  required?: string;
  available?: string;
  shortfall?: string;
  connection_cost?: string;
  period_cost?: string;
}

// ---- calls ------------------------------------------------------------------

/** getServices reads §17a/b/c for one Account of one contract. */
export function getServices(contractID: number, accountID: number): Promise<PAServices> {
  return api.get<PAServices>(`/pa/services/${accountID}?contract_id=${contractID}`);
}

/**
 * connectService is §17b's one narrow verb, and there is deliberately no PUT beside it: a
 * full-replacement body over a live service instance is how a forgotten field resets real
 * data (this repository's D109 class, confirmed live on the neighbouring staff route).
 *
 * `quantity` and `to_date` are sent only when the subscriber chose them, so the server's own
 * defaults («1», «no end») are what applies otherwise — a client that always sent a value
 * would be deciding them here.
 */
export function connectService(
  contractID: number,
  accountID: number,
  request: PAConnectServiceRequest,
): Promise<PAService> {
  return api.post<PAService>(
    `/pa/services/${accountID}/connect?contract_id=${contractID}`,
    request,
  );
}

/**
 * disconnectService STOPS a periodic instance. It refunds nothing — see rule 1.
 *
 * It is idempotent on the server (a `valid_to` already at or before now changes nothing), so
 * a double submit cannot move the stop date forward and bill another period.
 */
export function disconnectService(
  contractID: number,
  accountID: number,
  accountServiceID: number,
): Promise<PAService> {
  const body: PADisconnectServiceRequest = { account_service_id: accountServiceID };
  return api.post<PAService>(
    `/pa/services/${accountID}/disconnect?contract_id=${contractID}`,
    body,
  );
}
